import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useViewAs } from '@/contexts/ViewAsContext';

interface CourseAccessResult {
  hasAccess: boolean | null; // null = still checking
  loading: boolean;
  courseId: string | null;
}

/**
 * Check if current user has access to a course.
 * Accepts a courseId directly, or resolves it from a lessonId or taskId.
 */
export const useCourseAccess = ({
  courseId,
  lessonId,
  taskId,
}: {
  courseId?: string | null;
  lessonId?: string | null;
  taskId?: string | null;
}): CourseAccessResult => {
  const { user, isStaff, loading: authLoading } = useAuth();
  const { active, overrides } = useViewAs();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [resolvedCourseId, setResolvedCourseId] = useState<string | null>(courseId ?? null);

  useEffect(() => {
    // Wait for auth to finish loading before checking access
    if (authLoading) return;

    const check = async () => {
      setLoading(true);
      setHasAccess(null);

      // Admin always has access
      if (isStaff && !(active && overrides.simulateRegularUser)) {
        setHasAccess(true);
        setLoading(false);
        return;
      }

      // Check if courses are disabled globally
      const { data: appSettings } = await supabase
        .from('app_settings' as any)
        .select('courses_enabled')
        .eq('id', 1)
        .single();
      if (appSettings && !(appSettings as any).courses_enabled) {
        setHasAccess(false);
        setLoading(false);
        return;
      }

      // Resolve courseId from lessonId or taskId if needed
      let cId = courseId ?? null;

      if (!cId && lessonId) {
        const { data } = await supabase
          .from('lessons')
          .select('course_id')
          .eq('id', lessonId)
          .single();
        cId = data?.course_id ?? null;
      }

      if (!cId && taskId) {
        const { data } = await supabase
          .from('tasks')
          .select('course_id')
          .eq('id', taskId)
          .single();
        cId = data?.course_id ?? null;
      }

      setResolvedCourseId(cId);

      // No course linked = free/unlinked content
      if (!cId) {
        setHasAccess(true);
        setLoading(false);
        return;
      }

      // Check if the course is free (no subscription, no price)
      const { data: course } = await supabase
        .from('courses')
        .select('included_in_subscription, stripe_price_id')
        .eq('id', cId)
        .single();

      if (!course) {
        setHasAccess(true);
        setLoading(false);
        return;
      }

      if (!course.included_in_subscription && !course.stripe_price_id) {
        setHasAccess(true);
        setLoading(false);
        return;
      }

      // Not logged in = no access to paid course
      if (!user) {
        setHasAccess(false);
        setLoading(false);
        return;
      }

      // ViewAs simulation: decide access based on simulated tier instead of server check
      if (active && overrides.simulateRegularUser && overrides.tier) {
        // subscription-included courses: accessible if tier is standard or premium
        if (course.included_in_subscription) {
          setHasAccess(overrides.tier !== 'free');
        } else {
          // individual-purchase course: simulate no purchase for all tiers
          setHasAccess(false);
        }
        setLoading(false);
        return;
      }

      // Server-side check
      try {
        const { data, error } = await supabase.functions.invoke('check-course-access', {
          body: { courseId: cId },
        });
        if (error) throw error;
        setHasAccess(data?.hasAccess ?? false);
      } catch {
        setHasAccess(false);
      }

      setLoading(false);
    };

    check();
  }, [courseId, lessonId, taskId, user, isStaff, authLoading, active, overrides]);

  return { hasAccess, loading, courseId: resolvedCourseId };
};
